import React from 'react';
import { View, Text } from 'react-native';
import { Input, Button, Icon } from 'react-native-elements';
import { Formik } from 'formik';
import { styles } from 'styles/forms/loginForm';
import loginValidationSchema, { authCodeValidationSchema } from './schemas/loginForm';
import useChangeAuthData from 'hooks/useChangeAuthData';
import { loginEmail, loginWithAuthorizationCode } from 'services/authService';
import AppContext from 'contexts/AppContext';
import AuthData from 'types/models/AuthData';

type FormType = 'email-psw' | 'auth-code';
export default (): JSX.Element => {
  const { expoPushToken } = React.useContext(AppContext);
  const { storeAuthData } = useChangeAuthData();
  const [showInvalidCodeMessage, setShowInvalidCodeMessage] = React.useState<boolean>(false);
  const [showPassword, setShowPassword] = React.useState<boolean>(true);
  const [formType, setFormType] = React.useState<FormType>('email-psw');

  const saveAuthData = async (email: string, password: string) => {
    let authData: AuthData | null;
    if (expoPushToken) {
      authData = await loginEmail(email, password, expoPushToken);
    } else {
      authData = await loginEmail(email, password);
    }

    if (authData) {
      await storeAuthData(authData);
    } else {
      setShowInvalidCodeMessage(true);
    }
  };

  const saveAuthCodeData = async (code: string) => {
    const authData = await loginWithAuthorizationCode(code);
    if (authData) {
      await storeAuthData(authData);
      return;
    }

    setShowInvalidCodeMessage(true);
  };

  const handeSubmit = async (values: any) => {
    const { email, password, code } = values;
    if (formType === 'email-psw') {
      saveAuthData(email, password);
    } else {
      saveAuthCodeData(code);
    }
  };

  const handleChangeForm = (type: FormType) => {
    setShowInvalidCodeMessage(false);
    setFormType(type);
  };

  return (
    <Formik
      validationSchema={formType === 'email-psw' ? loginValidationSchema() : authCodeValidationSchema()}
      initialValues={formType === 'email-psw' ? { email: '', password: '' } : { code: '' }}
      enableReinitialize
      onSubmit={handeSubmit}
    >
      {({ handleChange, handleBlur, handleSubmit, values, errors, isValid, touched }) => (
        <>
          <View style={styles.container}>
            <Text style={styles.title}>Iniciar Sesión</Text>

            {formType === 'email-psw' && (
              <>
                <View style={styles.inputGroup}>
                  <Text style={styles.label}>Usuario</Text>
                  <Input
                    placeholder="Email"
                    onChangeText={handleChange('email')}
                    onBlur={handleBlur('email')}
                    value={values.email}
                    inputContainerStyle={styles.inputBox}
                    inputStyle={styles.input}
                    placeholderTextColor="#999"
                    keyboardType="email-address"
                    autoCapitalize="none"
                  />
                  {errors.email && touched.email && <Text style={styles.errorText}>{errors.email}</Text>}
                </View>

                <View style={styles.inputGroup}>
                  <Text style={styles.label}>Contraseña</Text>
                  <Input
                    placeholder="Contraseña"
                    onChangeText={handleChange('password')}
                    onBlur={handleBlur('password')}
                    value={values.password}
                    inputContainerStyle={styles.inputBox}
                    inputStyle={styles.input}
                    placeholderTextColor="#999"
                    secureTextEntry={showPassword}
                    autoCapitalize="none"
                    rightIcon={
                      <Icon
                        name={showPassword ? 'eye-off-outline' : 'eye-outline'}
                        type="ionicon"
                        onPress={() => setShowPassword(!showPassword)}
                      />
                    }
                  />
                  {errors.password && touched.password && <Text style={styles.errorText}>{errors.password}</Text>}
                </View>
              </>
            )}
            {formType === 'auth-code' && (
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Código de Autorización</Text>
                <Input
                  placeholder="123456"
                  onChangeText={handleChange('code')}
                  onBlur={handleBlur('code')}
                  value={values.code}
                  keyboardType="numeric"
                  maxLength={6}
                  placeholderTextColor="#999"
                  inputStyle={styles.input}
                  inputContainerStyle={styles.inputBox}
                  onSubmitEditing={() => handleSubmit()}
                />
                {errors.code && touched.code && <Text style={styles.errorText}>{errors.code}</Text>}
              </View>
            )}

            {showInvalidCodeMessage && (
              <Text style={{ color: '#F00' }}>
                {formType === 'email-psw'
                  ? 'Correo electrónico o contraseña erronea.'
                  : 'Código de autorización incorrecto.'}
              </Text>
            )}
            <Button
              title="Ingresar Sesión"
              onPress={() => handleSubmit()}
              buttonStyle={styles.button}
              titleStyle={styles.buttonTitle}
              containerStyle={styles.buttonContainer}
              disabled={!isValid}
            />

            <Button
              onPress={() => {
                handleChangeForm(formType === 'email-psw' ? 'auth-code' : 'email-psw');
              }}
              title={formType === 'email-psw' ? 'Ingresar con código de autorización' : 'Ingresar con email'}
              type="outline"
              buttonStyle={styles.buttonOutline}
              titleStyle={[styles.buttonTitle, { color: '#0F6657' }]}
              containerStyle={styles.buttonContainer}
            />
          </View>
        </>
      )}
    </Formik>
  );
};
